'use client'

import type { FormEvent } from 'react'
import { useState } from 'react'
import type { InvestableAssetsRange } from '@nwwp/database'
import { Checkbox } from '@nwwp/ui-shadcn'
import { LeadGateFields, useLeadGateFields } from '../../../chrome/LeadGateFields'
import { PhoneDisclosure } from '../../../lead-magnets/components/PhoneDisclosure'
import { getTrackingParams } from '../../../utils/tracking'
import type { CareerBreakState, CreateCareerBreakLeadFn } from '../types'

/**
 * The results gate: the visitor sets their inputs freely, then unlocks the results.
 *
 * Submitting creates the Lead, which is what sends the results email downstream, so this
 * is the page's only capture point — a second form after the results would create a
 * duplicate Lead (`POST /api/leads` does not dedupe on email).
 *
 * Phone is required because the consent block is express written TCPA consent, and
 * `nwwp_career-break` is stamped CONFIRMED at Lead create (see
 * packages/salesforce/src/mappers/tcpa-source-map.ts).
 */

/** Canonical qualification buckets. The prototype's four collapse $100K–$500K, which has no single stored value. */
const AUM_OPTIONS: { value: InvestableAssetsRange; label: string }[] = [
  { value: 'UNDER_100K', label: 'Less than $100K' },
  { value: 'FROM_100_TO_250K', label: '$100K-$249K' },
  { value: 'FROM_250_TO_500K', label: '$250K-$499K' },
  { value: 'FROM_500K_TO_1M', label: '$500K-$999K' },
  { value: 'OVER_1M', label: '$1MM+' },
]

export function UnlockGate({
  createLead,
  state,
  onUnlock,
}: {
  createLead: CreateCareerBreakLeadFn
  state: CareerBreakState
  onUnlock: () => void
}) {
  const gate = useLeadGateFields()
  const [wantsCall, setWantsCall] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!gate.contact || submitting) {
      if (!gate.contact) gate.revealErrors()
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      // The gate asks for the household total; the panel asks each earner separately.
      // The Lead takes this figure as-is and the model keeps the panel's inputs, so a
      // couple's partner income is not counted twice.
      const created = await createLead({ ...gate.contact, wants_call: wantsCall }, state, getTrackingParams())
      if (!created) throw new Error('Lead creation failed')
      onUnlock()
    } catch (error) {
      console.error('Career Break lead creation failed', error)
      setSubmitError('Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <section className="w-full rounded-xl border border-border bg-card p-6 shadow-lg tablet:p-8">
      <h2 className="text-title-sm text-gray-darker">Unlock the calculator</h2>

      <form noValidate onSubmit={handleSubmit} className="mt-5 flex flex-col gap-4">
        <LeadGateFields
          gate={gate}
          idPrefix="cb"
          assetsOptions={AUM_OPTIONS}
          assetsErrorMessage="Please select your estimated investable assets."
          incomePlaceholder="120,000"
        />

        <label className="flex items-start gap-2.5 text-body-sm text-neutral-dark">
          <Checkbox
            checked={wantsCall}
            onCheckedChange={(checked) => setWantsCall(checked === true)}
            aria-label="I'd also like a free 20-minute call with an advisor about this."
            className="mt-0.5"
          />
          I&apos;d also like a free 20-minute call with an advisor about this.
        </label>

        {/* The approved consent block, shared with /schedule_form and the lead magnets. */}
        <PhoneDisclosure ctaText="See my results" className="text-body-xs text-neutral-dark" />

        {submitError ? (
          <p role="alert" className="text-body-sm text-red">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="self-start rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-40"
        >
          {submitting ? 'Unlocking…' : 'See my results →'}
        </button>
      </form>
    </section>
  )
}

/** Shown after a successful gate submit. Delivery depends on Customer.io message IDs being set. */
export function ResultsEmailedNote() {
  return (
    <section className="rounded-xl border border-border bg-card p-6 tablet:p-8">
      <h2 className="text-title-sm text-gray-darker">Thanks — your results are unlocked.</h2>
      <p className="mt-2 text-body-sm text-neutral-dark">
        A copy may also arrive by email so you can come back to them as you decide.
      </p>
    </section>
  )
}
